import React, { useEffect, useRef, useState } from 'react';
import LatoRegular from "../../../../utils/fonts/Lato-Regular.ttf";
import LatoBold from '../../../../utils/fonts/Lato-Bold.ttf';
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import Logo from "../../../../images/logo.png";
import moment from 'moment';
import { formatScientificNotation } from '../../../../utils/functionalUtils';

// Color constants for consistent styling
const COLORS = {
  primary: '#2c3e50',
  secondary: '#34495e',
  accent: '#3498db',
  lightGray: '#ecf0f1',
  darkGray: '#7f8c8d',
  border: '#bdc3c7',
  text: '#2c3e50',
  headerBg: '#f8f9fa'
};

// Register the Lato font
Font.register({
  family: 'Lato',
  fonts: [
    { src: LatoRegular },
    { src: LatoBold, fontWeight: 'bold' }
  ]
});

const LoadingOrderPDF = ({ page_data }) => {
  const targetRef = useRef();
  const [pdf_data, setPDFData] = useState(page_data);
  const [groupByBranch, setGroupedByBranch] = useState([]);

  useEffect(() => {
    setPDFData(page_data);
  }, [page_data]);

  const groupByBranchLocation = (data) => {
    return data?.reduce((acc, item) => ({
      ...acc,
      [item.branch_location]: [
        ...(acc[item.branch_location] || []),
        item
      ]
    }), {});
  };

  useEffect(() => {
    if (pdf_data?.products && pdf_data?.products?.length > 0)
    {
      const groupedData = groupByBranchLocation(pdf_data?.products);
      setGroupedByBranch(groupedData);
    }
  }, [pdf_data]);

  return (
    <>
      {pdf_data && (
        <Document title={`${pdf_data?.loading_order_ref_no} Loading Order`} ref={targetRef}>
          <Page
            size="A4"
            style={{
              flexDirection: "row",
              backgroundColor: "#ffffff",
              padding: 20
            }}
            orientation="landscape"
          >
            {/* Header Section */}
            <View style={{ width: "100%" }}>
              <View style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
                borderBottom: `1px solid ${COLORS.border}`,
                paddingBottom: 15
              }}>
                <View style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <View style={{ width: 100, marginRight: 15 }}>
                    <Image src={Logo} />
                  </View>
                  <View style={{ fontSize: 10 }}>
                    <Text style={{ marginBottom: 3, color: COLORS.darkGray }}>Plot 10111 off Mumbwa Road, Chinika Area</Text>
                    <Text style={{ marginBottom: 3, color: COLORS.darkGray }}>P.O. Box 30203, Lusaka, Zambia.</Text>
                    <Text style={{ marginBottom: 3, color: COLORS.darkGray }}>Mobile: +260 764 299 293</Text>
                    <Text style={{ color: COLORS.darkGray }}>Email: info@iagzambia.com</Text>
                  </View>
                </View>

                <View style={{ textAlign: "right" }}>
                  <Text style={{
                    fontSize: 18,
                    fontWeight: "bold",
                    color: COLORS.primary,
                    marginBottom: 10
                  }}>
                    LOADING ORDER
                  </Text>

                  <View style={{ fontSize: 10 }}>
                    <View style={{ marginBottom: 3 }}>
                      <Text style={{ color: COLORS.darkGray }}>Loading Order Number: </Text>
                      <Text style={{ fontWeight: "bold" }}>{pdf_data?.loading_order_ref_no || "---"}</Text>
                    </View>
                    <View>
                      <Text style={{ color: COLORS.darkGray }}>Date Created: </Text>
                      <Text style={{ fontWeight: "bold" }}>
                        {pdf_data?.inserted_at ? moment(pdf_data?.inserted_at).format("MMMM Do, YYYY") : "---"}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              {/* Branch and Product Information */}
              {Object.keys(groupByBranch).length > 0 && (
                Object.entries(groupByBranch).map(([branch, items]) => (
                  <View key={branch} style={{ marginBottom: 20 }}>
                    <View style={{
                      backgroundColor: COLORS.headerBg,
                      padding: 8,
                      marginBottom: 10,
                      borderRadius: 3
                    }}>
                      <Text style={{ fontSize: 12, fontWeight: "bold", color: COLORS.primary }}>
                        BRANCH: {branch.toUpperCase()}
                      </Text>
                    </View>

                    <View style={{ marginBottom: 10 }}>
                      <View style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: 3
                      }}>
                        <Text style={{ fontSize: 11 }}>
                          <Text style={{ fontWeight: "bold" }}>Customer: </Text>
                          {pdf_data?.consigner?.username || "---"}
                        </Text>
                        <Text style={{ fontSize: 11 }}>
                          <Text style={{ fontWeight: "bold" }}>Issued by: </Text>
                          {pdf_data?.issued_by || "---"}
                        </Text>
                      </View>
                      <Text style={{ fontSize: 11 }}>
                        <Text style={{ fontWeight: "bold" }}>Contact Person: </Text>
                        {pdf_data?.contact_person ? `${pdf_data?.contact_person} (${pdf_data?.contact_mobile})` : "---"}
                      </Text>
                    </View>

                    {/* Products Table */}
                    <View style={{
                      border: `1px solid ${COLORS.border}`,
                      marginBottom: 15
                    }}>
                      {/* Table Header */}
                      <View style={{
                        display: "flex",
                        flexDirection: "row",
                        backgroundColor: COLORS.headerBg,
                        borderBottom: `1px solid ${COLORS.border}`
                      }}>
                        {[
                          { label: "Product Name", width: "18%" },
                          { label: "Packet Size", width: "15%" },
                          { label: "Package No", width: "15%" },
                          { label: "Quantity", width: "15%" },
                          { label: "Quantity (MT)", width: "17%" },
                          { label: "Delivery Location", width: "20%" }
                        ].map((header, index) => (
                          <View key={index} style={{
                            width: header.width,
                            padding: 5,
                            borderRight: index < 5 ? `1px solid ${COLORS.border}` : 'none'
                          }}>
                            <Text style={{
                              fontWeight: "bold",
                              fontSize: 10,
                              textAlign: "center",
                              color: COLORS.primary
                            }}>
                              {header.label}
                            </Text>
                          </View>
                        ))}
                      </View>

                      {/* Table Rows */}
                      {items.map((item, index) => (
                        <View
                          key={index}
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            borderBottom: index < items.length - 1 ? `1px solid ${COLORS.lightGray}` : 'none',
                            backgroundColor: index % 2 === 0 ? '#ffffff' : COLORS.lightGray
                          }}
                        >
                          {[
                            { value: item?.product_name || "---", width: "18%" },
                            { value: item?.packaging_total_number || "---", width: "15%" },
                            { value: item?.total_capacity_selected_measurement ? `${parseFloat(item?.total_capacity_selected_measurement || 0.00)?.toFixed(0)}` : "---", width: "15%" },
                            { value: item?.base_product_selected_measurement ? formatScientificNotation(item?.base_product_selected_measurement) : "---", width: "15%" },
                            { value: item?.total_mt_capacity ? `${parseFloat(item?.total_mt_capacity || 0.00)?.toFixed(2)} MTs` : "---", width: "17%" },
                            { value: item?.delivery_name || "---", width: "20%" }
                          ].map((cell, cellIndex) => (
                            <View
                              key={cellIndex}
                              style={{
                                width: cell.width,
                                padding: 5,
                                borderRight: cellIndex < 5 ? `1px solid ${COLORS.lightGray}` : 'none'
                              }}
                            >
                              <Text style={{
                                fontSize: 9,
                                textAlign: "center",
                                color: COLORS.text
                              }}>
                                {cell.value}
                              </Text>
                            </View>
                          ))}
                        </View>
                      ))}
                    </View>
                  </View>
                ))
              )}

              {/* Truck Details */}
              <View style={{ marginBottom: 20 }}>
                <View style={{
                  backgroundColor: COLORS.headerBg,
                  padding: 8,
                  marginBottom: 10,
                  borderRadius: 3
                }}>
                  <Text style={{ fontSize: 12, fontWeight: "bold", color: COLORS.primary }}>
                    TRUCK DETAILS
                  </Text>
                </View>

                <View style={{ border: `1px solid ${COLORS.border}` }}>
                  {/* Table Header */}
                  <View style={{
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: COLORS.headerBg,
                    borderBottom: `1px solid ${COLORS.border}`
                  }}>
                    {[
                      { label: "Truck Reg", width: "20%" },
                      { label: "Trailer Reg 1", width: "20%" },
                      { label: "Trailer Reg 2", width: "20%" },
                      { label: "Driver Name", width: "20%" },
                      { label: "Transporter", width: "20%" }
                    ].map((header, index) => (
                      <View key={index} style={{
                        width: header.width,
                        padding: 5,
                        borderRight: index < 4 ? `1px solid ${COLORS.border}` : 'none'
                      }}>
                        <Text style={{
                          fontWeight: "bold",
                          fontSize: 10,
                          textAlign: "center",
                          color: COLORS.primary
                        }}>
                          {header.label}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {/* Table Row */}
                  <View style={{
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: '#ffffff'
                  }}>
                    {[
                      { value: pdf_data?.truck_license_plate || "---", width: "20%" },
                      { value: pdf_data?.trailer_one_no || "---", width: "20%" },
                      { value: pdf_data?.trailer_two_no || "---", width: "20%" },
                      { value: pdf_data?.truck_driver_name || "---", width: "20%" },
                      { value: pdf_data?.transporter_name || "---", width: "20%" }
                    ].map((cell, cellIndex) => (
                      <View
                        key={cellIndex}
                        style={{
                          width: cell.width,
                          padding: 5,
                          borderRight: cellIndex < 4 ? `1px solid ${COLORS.lightGray}` : 'none'
                        }}
                      >
                        <Text style={{
                          fontSize: 9,
                          textAlign: "center",
                          color: COLORS.text
                        }}>
                          {cell.value}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>

              {/* Loading Instructions */}
              <View>
                <View style={{
                  backgroundColor: COLORS.headerBg,
                  padding: 8,
                  marginBottom: 10,
                  borderRadius: 3
                }}>
                  <Text style={{ fontSize: 12, fontWeight: "bold", color: COLORS.primary }}>
                    LOADING INSTRUCTIONS
                  </Text>
                </View>

                {pdf_data?.driver_instructions?.length > 0 ? (
                  <View style={{ border: `1px solid ${COLORS.border}` }}>
                    {/* Table Header */}
                    <View style={{
                      display: "flex",
                      flexDirection: "row",
                      backgroundColor: COLORS.headerBg,
                      borderBottom: `1px solid ${COLORS.border}`
                    }}>
                      <View style={{ width: "10%", padding: 5 }}>
                        <Text style={{ fontWeight: "bold", fontSize: 10, textAlign: "center", color: COLORS.primary }}>
                          No.
                        </Text>
                      </View>
                      <View style={{ width: "90%", padding: 5 }}>
                        <Text style={{ fontWeight: "bold", fontSize: 10, color: COLORS.primary }}>
                          Instructions
                        </Text>
                      </View>
                    </View>

                    {/* Table Rows */}
                    {pdf_data?.driver_instructions?.map((instruction, index) => (
                      <View
                        key={index}
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          borderBottom: index < pdf_data.driver_instructions.length - 1 ? `1px solid ${COLORS.lightGray}` : 'none',
                          backgroundColor: index % 2 === 0 ? '#ffffff' : COLORS.lightGray
                        }}
                      >
                        <View style={{ width: "10%", padding: 5 }}>
                          <Text style={{ fontSize: 9, textAlign: "center", color: COLORS.text }}>
                            {index + 1}
                          </Text>
                        </View>
                        <View style={{ width: "90%", padding: 5 }}>
                          <Text style={{ fontSize: 9, color: COLORS.text }}>
                            {instruction}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={{
                    border: `1px solid ${COLORS.border}`,
                    padding: 10,
                    textAlign: "center",
                    backgroundColor: COLORS.lightGray
                  }}>
                    <Text style={{ fontSize: 10, fontStyle: "italic", color: COLORS.darkGray }}>
                      No instructions available
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </Page>
        </Document>
      )}
    </>
  );
};

export default LoadingOrderPDF;